import React, { useContext, useMemo, type FC } from "react";

import { useFilterListProps } from "@usereactify/search";

import { ComponentProps } from "~/types/components";
import { SearchResultSwatch } from "~/types/search";

import { FilterContext } from "~/providers/FilterProvider";
import { useRouteLoaderData } from "~/hooks/useRouteLoaderData";

export type SizeType = {
  _key: string;
  bared: string;
  us_au: string;
  cm: string;
  inch: string;
};

type FilterSectionBoxInputProps = ComponentProps &
  ReturnType<typeof useFilterListProps> & {
    handleSizeClick?: () => void;
    layout?: "heroBanner";
  };

type FilterSectionBoxOutputProps = Omit<
  FilterSectionBoxInputProps,
  "options"
> & {
  options: {
    label: string;
    count: number;
    checked: boolean;
    us_size?: string;
  }[];
};

const prepareBoxOptions = (
  options: SearchResultSwatch[],
): SearchResultSwatch[] => {
  const data =
    options?.reduce((result: { [key: string]: SearchResultSwatch }, item) => {
      result[item.key] = item;

      return result;
    }, {}) || {};

  return Object.values<SearchResultSwatch>(data);
};

export const withFilterSectionBox =
  (Component: FC<FilterSectionBoxOutputProps>) =>
  ({
    name = "FilterSectionBox",
    options,
    handleSizeClick,
    layout,
    ...props
  }: FilterSectionBoxInputProps) => {
    const { gender, handleScroll } = useContext(FilterContext);
    const { sizeGuide } = useRouteLoaderData("routes/$site");

    const convertedSizing: SizeType[] = useMemo(() => {
      if (gender === "men") {
        return [...sizeGuide.men];
      } else {
        return [...sizeGuide.women];
      }
    }, [gender, sizeGuide]);

    const boxOptions = prepareBoxOptions(options) || [];

    const mappedOptions = boxOptions.map(
      ({ key, doc_count, label, checked }) => {
        const us_size = convertedSizing.find(
          (size) => size.bared === key,
        )?.us_au;

        return {
          label,
          count: doc_count,
          checked,
          us_size,
        };
      },
    );

    const handleChange = (key: string) => {
      props.handleChange(key);
      handleScroll();
    };

    Component.displayName = name;

    return (
      <Component
        {...props}
        options={mappedOptions}
        handleChange={handleChange}
      />
    );
  };
